




















































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import MmHintedButton from '../../../components/MmHintedButton.vue';
import MmStates from '../../../components/MmStates.vue';

import { fetchGroupExamples, MmGroupExample } from '../../../api/catalog';
import { createCancelSource } from '../../../api/mmHttp';
import { formatDiagnostics, isCanceled, MmError, toMmError } from '../../../core/errors';
import { MmSelectOption } from '../../../types/domain';
import { MmErrorCode } from '../../../types/enums';

const KEY = 'priceMonitoring.mobileMonitoring.';

const SECTION_HINT = 'tasks/group-examples';

const SUBCATEGORY_SEPARATOR = ' · ';

/** undefined — ещё не грузили, пустой массив — грузили и состава нет; повторно не запрашиваем */
interface MmGroupExamplesCache {
	[key: string]: MmGroupExample[] | undefined;
}

@Component({
	components: {
		MmHintedButton,
		MmStates,
	},
})
export default class GroupExamplesPopover extends Vue {
	@Prop({ default: null }) readonly tradingId!: number | null;

	/** «Не выбрано» — пустой массив; realValuesOrEmpty к товарным уровням не применяем */
	@Prop({ required: true }) readonly groupIds!: number[];

	@Prop({ default: false }) readonly disabled!: boolean;

	isOpen: boolean = false;
	isLoading: boolean = false;
	loadError: MmError | null = null;
	searchQuery: string = '';

	/** Кэш переприсваиваем целиком: Vue 2 не замечает добавления нового свойства объекта */
	private cache: MmGroupExamplesCache = {};

	private examplesSource: any = null;

	/** Сортируем копию и с компаратором: sort мутирует массив и без него сравнивает как строки */
	get cacheKey(): string {
		const ids: number[] = Array.isArray(this.groupIds) ? this.groupIds.slice() : [];

		ids.sort((left: number, right: number) => left - right);

		return ids.join(',');
	}

	get rows(): MmGroupExample[] {
		const cached = this.cache[this.cacheKey];

		return cached === undefined ? [] : cached;
	}

	get hasRows(): boolean {
		return this.rows.length > 0 && !this.isLoading && this.loadError === null;
	}

	get visibleRows(): MmGroupExample[] {
		const query = this.searchQuery.trim().toLowerCase();

		if (query === '') {
			return this.rows;
		}

		const result: MmGroupExample[] = [];

		for (let i = 0; i < this.rows.length; i += 1) {
			const row = this.rows[i];
			const label = this.categoryLabel(row).toLowerCase();

			if (label.indexOf(query) >= 0) {
				result.push(row);
				continue;
			}

			const matched = this.subcategoriesOf(row).filter((item) => {
				const itemLabel = item && item.label ? String(item.label) : '';

				return itemLabel.toLowerCase().indexOf(query) >= 0;
			});

			if (matched.length > 0) {
				result.push({ category: row.category, subcategories: matched });
			}
		}

		return result;
	}

	get isTriggerDisabled(): boolean {
		return this.disabled === true || this.tradingId === null || this.selectedGroupsCount === 0;
	}

	/** Порядок веток причины обязан совпадать с порядком проверок, иначе назовём не тот запрет */
	get triggerDisabledReason(): string {
		if (!this.isTriggerDisabled) {
			return '';
		}

		if (this.disabled === true) {
			return this.$t(KEY + 'taskForm.blockSubmitting') as string;
		}

		if (this.tradingId === null) {
			return this.$t(KEY + 'taskForm.blockNoNetwork') as string;
		}

		return this.$t(KEY + 'taskForm.examplesDisabled') as string;
	}

	private get selectedGroupsCount(): number {
		return Array.isArray(this.groupIds) ? this.groupIds.length : 0;
	}

	get isStateVisible(): boolean {
		return this.isLoading || this.loadError !== null || this.visibleRows.length === 0;
	}

	get contentState(): string {
		if (this.isLoading) {
			return 'loading';
		}

		if (this.loadError !== null) {
			return this.loadError.code === MmErrorCode.FORBIDDEN ? 'forbidden' : 'error';
		}

		return 'empty';
	}

	get isRetryable(): boolean {
		return this.loadError !== null && this.loadError.retryable === true;
	}

	get diagnosticsText(): string {
		return this.loadError === null ? '' : formatDiagnostics(this.loadError.diagnostics);
	}

	get stateTitleOverride(): string {
		if (this.contentState !== 'empty') {
			return '';
		}

		if (this.rows.length > 0) {
			return this.$t(KEY + 'table.empty') as string;
		}

		return this.$t(KEY + 'examples.empty') as string;
	}

	get stateTextOverride(): string {
		return this.contentState === 'error' ? (this.$t(KEY + 'taskForm.examplesError') as string) : '';
	}

	get buttonText(): string {
		return this.$t(KEY + 'taskForm.examplesButton') as string;
	}

	get searchPlaceholder(): string {
		return this.$t(KEY + 'taskForm.examplesSearch') as string;
	}

	beforeDestroy(): void {
		this.cancelPending();
	}

	@Watch('cacheKey')
	onGroupsChanged(): void {
		this.cancelPending();
		this.isLoading = false;
		this.loadError = null;
		this.searchQuery = '';

		if (this.isOpen) {
			this.ensureLoaded();
		}
	}

	/** Смена сети сносит кэш целиком: ключ кэша — только группы, иначе сети увидели бы чужой состав */
	@Watch('tradingId')
	onTradingChanged(): void {
		this.cancelPending();
		this.cache = {};
		this.isLoading = false;
		this.loadError = null;
		this.searchQuery = '';
		this.isOpen = false;
	}

	@Watch('disabled')
	onDisabledChanged(next: boolean): void {
		if (next === true) {
			this.isOpen = false;
		}
	}

	/** Отключённую кнопку гасим здесь: a-popover ловит нажатие на обёртке подсказки, а не на кнопке */
	handleVisibleChange(next: boolean): void {
		if (next === true && this.isTriggerDisabled) {
			this.isOpen = false;

			return;
		}

		this.isOpen = next === true;

		if (this.isOpen) {
			this.ensureLoaded();

			return;
		}

		// Поп-ап при закрытии не разрушается, поэтому набранный запрос чистим руками
		this.searchQuery = '';
	}

	handleRetry(): void {
		this.loadExamples();
	}

	rowKey(row: MmGroupExample): string {
		return row && row.category ? String(row.category.value) : '';
	}

	categoryLabel(row: MmGroupExample): string {
		return row && row.category && row.category.label ? String(row.category.label) : '';
	}

	subcategoriesLabel(row: MmGroupExample): string {
		const labels: string[] = [];
		const items = this.subcategoriesOf(row);

		for (let i = 0; i < items.length; i += 1) {
			const label = items[i] && items[i].label ? String(items[i].label) : '';

			if (label !== '') {
				labels.push(label);
			}
		}

		if (labels.length === 0) {
			return '';
		}

		return labels.join(SUBCATEGORY_SEPARATOR);
	}

	private subcategoriesOf(row: MmGroupExample): MmSelectOption[] {
		return row && Array.isArray(row.subcategories) ? row.subcategories : [];
	}

	/** Проверяем на undefined, а не на длину: пустой массив — это загруженный ответ «категорий нет» */
	private ensureLoaded(): void {
		if (this.tradingId === null || this.cacheKey === '') {
			return;
		}

		if (this.cache[this.cacheKey] !== undefined) {
			this.loadError = null;

			return;
		}

		this.loadExamples();
	}

	private loadExamples(): void {
		if (this.tradingId === null) {
			return;
		}

		// Ключ фиксируем до запроса: пока идёт ответ, набор групп может смениться
		const key = this.cacheKey;

		if (key === '') {
			return;
		}

		this.cancelPending();

		const source = createCancelSource();

		this.examplesSource = source;
		this.isLoading = true;
		this.loadError = null;

		fetchGroupExamples(this.tradingId as number, this.groupIds.slice(), { cancelToken: source.token })
			.then((items: MmGroupExample[]) => {
				if (this.examplesSource !== source) {
					return;
				}

				this.writeCache(key, Array.isArray(items) ? items : []);
			})
			.catch((error: any) => {
				if (isCanceled(error) || this.examplesSource !== source) {
					return;
				}

				this.loadError = toMmError(error, SECTION_HINT);
			})
			.finally(() => {
				if (this.examplesSource !== source) {
					return;
				}

				this.examplesSource = null;
				this.isLoading = false;
			});
	}

	private writeCache(key: string, rows: MmGroupExample[]): void {
		const next: MmGroupExamplesCache = {};
		const keys: string[] = Object.keys(this.cache);

		for (let i = 0; i < keys.length; i += 1) {
			next[keys[i]] = this.cache[keys[i]];
		}

		next[key] = rows;

		this.cache = next;
	}

	private cancelPending(): void {
		if (this.examplesSource !== null) {
			this.examplesSource.cancel();
			this.examplesSource = null;
		}
	}
}
